import { getSensorGroupMap } from '@Actions/sensorGroup.action';
import { getSensorIdsMap } from '@Actions/sensors';
import {
	ActionBar,
	Button,
	ContentCard,
	EmptyTableBody,
	rem,
	StationIllustration,
	StylovyzeSearchBar,
	StylovyzeTableBody,
	StylovyzeTableFooter,
	StylovyzeTableRow,
	useSettings,
} from '@innovyze/stylovyze';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations';
import { ROWS_PER_PAGE, SearchProps, Sort, STATUS_ENUM } from '@Types';
import { LIFT_STATION_ROUTES } from '@Types/routes';
import { addRouteNamespace } from '@Utils/actions';
import formatDateTime from '@Utils/formatDateTime';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

interface StationsTableProps {
	setSearchProps: React.Dispatch<React.SetStateAction<SearchProps>>;
	searchProps: SearchProps;
}
const getSortKey = (sort?: Sort) => {
	if (sort) {
		const sortKeys = Object.keys(sort);
		if (sortKeys.length === 0) return null;
		return sortKeys[0];
	}
	return null;
};

const getSortOrder = (sort?: Sort) => {
	if (sort) {
		const sortValues = Object.values(sort);
		if (sortValues.length === 0) return null;
		return sortValues[0];
	}
	return null;
};

const StationsTable = ({ setSearchProps, searchProps }: StationsTableProps) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const liftStations =
		useSelector(getValue).stations?.paginatedStations?.stations || [];
	const initialized =
		useSelector(getValue).stations?.paginatedStations?.initialized || false;
	const pagination =
		useSelector(getValue).stations?.paginatedStations?.pagination;
	const { sensorIdMap, status: sensorIdMapStatus } =
		useSelector(getValue).sensors?.sensorIdMap;
	const { sensorGroupMap, status: sensorGroupMapStatus } =
		useSelector(getValue).sensorGroup?.sensorGroupMap;

	const { companySettings } = useSettings();
	const { dateFormat, hourCycle12, timeZoneIANA } = companySettings;
	const format = `${dateFormat} - ${hourCycle12 ? 'hh' : 'HH'}:mm ${
		hourCycle12 ? 'A' : ''
	}`;

	const [rows, setRows] = useState<StylovyzeTableRow[]>([]);

	useEffect(() => {
		if (initialized) {
			const sensorIdsSet = new Set<string>();
			liftStations.forEach(liftStation => {
				liftStation.sensors?.forEach(sensor => {
					sensorIdsSet.add(sensor.sensorId);
				});
			});
			dispatch(
				getSensorIdsMap({
					sensorIds: encodeURIComponent(
						Array.from(sensorIdsSet).join(','),
					),
					limit: 1000,
					configType: 'sensor',
				}),
			);
			const sensorGroupSet = new Set<string>();
			liftStations.forEach(liftStation => {
				liftStation.sensorGroups?.forEach(sensorGroup => {
					sensorGroupSet.add(sensorGroup.sensorGroupId);
				});
			});
			dispatch(
				getSensorGroupMap({
					sensorGroupIds: encodeURIComponent(
						Array.from(sensorGroupSet).join(','),
					),
					limit: 1000,
				}),
			);
		}
	}, [liftStations]);

	useEffect(() => {
		if (
			(sensorIdMapStatus === STATUS_ENUM.SUCCESS ||
				sensorIdMapStatus === STATUS_ENUM.ERROR) &&
			(sensorGroupMapStatus === STATUS_ENUM.SUCCESS ||
				sensorGroupMapStatus === STATUS_ENUM.ERROR)
		) {
			setRows(
				liftStations?.map(station => ({
					key: station.name,
					pathToGoWhenClicked: addRouteNamespace(
						`lift-station/${station._id}`,
					),
					cells: [
						station.name,
						{
							content: station?.sensorGroups
								?.map(i => {
									if (
										i.sensorGroupId in sensorGroupMap &&
										sensorGroupMap[i.sensorGroupId].name
									) {
										return sensorGroupMap[i.sensorGroupId]
											.name;
									}
									return i.sensorGroupId;
								})
								.join(', '),
							ellipsis: true,
						},
						{
							content: station.sensors
								?.map(i => {
									if (
										i.sensorId in sensorIdMap &&
										sensorIdMap[i.sensorId].sensorAlias
									) {
										return sensorIdMap[i.sensorId]
											.sensorAlias;
									}
									return i.sensorId;
								})
								.join(', '),
							ellipsis: true,
						},
						formatDateTime(
							new Date(station.lastModifiedDate),
							timeZoneIANA,
							format,
						),
					],
				})),
			);
		}
	}, [sensorIdMap, sensorGroupMap]);

	const header = (
		<ActionBar>
			<StylovyzeSearchBar
				label={t('Search')}
				dataCy="searchBarLiftStation"
				placeholder={t('Search')}
				handleSearchSubmit={newValue =>
					setSearchProps(oldSearch => ({
						...oldSearch,
						searchQuery: newValue,
						offset: 0,
					}))
				}
			/>
			<div>
				<Button
					data-cy="createStation"
					variant="contained"
					color="primary"
					style={{ maxWidth: '100%' }}
					onClick={() => {
						history.push(addRouteNamespace('lift-stations/create'));
					}}>
					{t('Create Facility')}
				</Button>
			</div>
		</ActionBar>
	);

	const footer = (
		<StylovyzeTableFooter
			rowsPerPage={{
				options: ROWS_PER_PAGE.map(name => {
					return {
						name: name.toString(),
						onClick: () => {
							setSearchProps(prevSearchParams => ({
								...prevSearchParams,
								limit: name,
								offset: 0,
							}));
						},
					};
				}),
				selectedIndex: 1,
			}}
			pagination={{
				totalNumberOfPages: pagination?.pages || 0,
				currentPage: searchProps.offset ? searchProps.offset + 1 : 1,
				dataCy: 'stations-table-pagination',
				onChange: (_e, page) => {
					setSearchProps(_searchProps => ({
						..._searchProps,
						offset: page - 1,
					}));
				},
			}}
		/>
	);
	return (
		<ContentCard
			header={header}
			footer={footer}
			applyBodyPadding={false}
			minHeight={rem(600)}>
			<StylovyzeTableBody
				dataCy="stations-table"
				headers={[
					{
						key: 'stationName',
						cell: t('Name'),
						align: 'left',
					},
					{
						key: 'sensors-groups',
						cell: t('Sensors groups'),
						width: 'auto',
						maxWidth: '200px',
						align: 'left',
					},
					{
						key: 'Sensors',
						cell: t('Sensors'),
						width: 'auto',
						maxWidth: '200px',
						align: 'left',
					},
					{
						key: 'lastModifiedDate',
						cell: t('Last update'),
						align: 'left',
						sortable: true,
					},
				]}
				rows={rows}
				initialized={
					initialized &&
					(sensorIdMapStatus === STATUS_ENUM.SUCCESS ||
						sensorIdMapStatus === STATUS_ENUM.ERROR) &&
					(sensorGroupMapStatus === STATUS_ENUM.SUCCESS ||
						sensorGroupMapStatus === STATUS_ENUM.ERROR)
				}
				useSkeleton={{ rowCount: searchProps.limit || 10 }}
				sort={{
					name: getSortKey(searchProps?.sort) || 'lastModifiedDate',
					order: getSortOrder(searchProps?.sort) || 'desc',
					onRequestSort: (_e, name, order) => {
						setSearchProps(_searchProps => ({
							..._searchProps,
							sort: {
								[name]: order,
							},
						}));
					},
				}}
				emptyTableBody={
					<EmptyTableBody
						dataCy="stations-empty-table-body"
						title={t('There are no facilities to display.')}
						subtitle={t(
							'A facility is a user-defined group of sensors that is collecting data in one of your physical locations.',
						)}
						icon={<StationIllustration />}
						color={'#4A6067'}
						CTA={
							<Button
								variant="contained"
								color="primary"
								onClick={() => {
									history.push(
										addRouteNamespace(
											LIFT_STATION_ROUTES.create,
										),
									);
								}}>
								{t('Create Facility')}
							</Button>
						}
					/>
				}
			/>
		</ContentCard>
	);
};

export default StationsTable;
